import {cardioSummary} from "@client/components/cardio/model";
import {Icon} from "@client/components/Icon";
import * as workouts from "@client/features/workouts";
import * as forms from "@client/forms";
import * as widgets from "@client/forms/widgets";
import * as hardware from "@client/hardware";
import {CheckIcon, MinusIcon} from "@heroicons/react/20/solid";
import {classNames, models, server} from "@reactivated";
import {Decimal} from "decimal.js";
import React from "react";
import {P, match} from "ts-pattern";

// Template sets in a request carry no marks.
type SetMarks = {
    uuid: string;
    type: server.journal.workouts.ExerciseSet.output["type"];
    side?: server.journal.workouts.ExerciseSet.output["side"];
    drop_of?: {uuid: string} | null;
};

// The index of the set a drop belongs to. A drop renders as one only directly
// under its parent (a chain's previous drop counts); anywhere else, after a
// drag say, it renders as a normal set and this is null.
export function getDropParent(sets: SetMarks[], index: number): number | null {
    if (index == 0) {
        return null;
    }
    return sets[index].drop_of?.uuid == sets[index - 1].uuid ? index - 1 : null;
}

// Counts working sets on the same side, so sided sets read 1L, 1R, 2L, 2R. A
// drop shares its parent's number.
export function getSetPosition(sets: SetMarks[], exerciseSetIndex: number): number {
    const parent = getDropParent(sets, exerciseSetIndex);
    if (parent != null) {
        return getSetPosition(sets, parent);
    }
    const {side} = sets[exerciseSetIndex];
    return (
        sets
            .slice(0, exerciseSetIndex)
            .filter(
                (set, index) =>
                    set.type == "WORKING" &&
                    set.side == side &&
                    getDropParent(sets, index) == null,
            ).length + 1
    );
}

export function ExerciseSetCheck({
    handler,
    readOnly,
    isImperial,
    restTimerBetweenExercises,
}: {
    readOnly: boolean;
    handler: workouts.ExerciseSet;
    isImperial: boolean;
    restTimerBetweenExercises: boolean;
}) {
    const workoutContext = React.useContext(workouts.WorkoutContext);
    const exerciseContext = React.useContext(workouts.ExerciseContext);

    if (handler.status == "COMPLETE") {
        return (
            <button
                type="button"
                role="checkbox"
                aria-checked={true}
                disabled={readOnly}
                onClick={() => {
                    handler.__update({status: "INCOMPLETE"});
                    hardware.haptic("Light");
                }}
                className="flex size-8 items-center justify-center rounded-full bg-green-100"
            >
                <CheckIcon className="size-5 text-green-600" aria-hidden="true" />
            </button>
        );
    } else if (handler.status == "SKIPPED") {
        return (
            <button
                type="button"
                disabled={readOnly}
                onClick={() => {
                    handler.__update({status: "INCOMPLETE"});
                    hardware.haptic("Light");
                }}
                className="flex size-8 items-center justify-center rounded-full bg-sky-100"
            >
                <MinusIcon className="size-5 text-sky-600" aria-hidden="true" />
            </button>
        );
    }

    return (
        <button
            type="button"
            role="checkbox"
            aria-checked={false}
            disabled={readOnly}
            onClick={() => {
                const suggestedWeight =
                    handler.suggested_weight != null
                        ? new Decimal(handler.suggested_weight)
                              .times(isImperial ? "0.45359237" : "1")
                              .toString()
                        : null;

                handler.__update({
                    weight: handler.weight ?? suggestedWeight,
                    status: "COMPLETE",
                });
                hardware.haptic("Medium");

                // Check if this is the last set of the current exercise
                const allExercises =
                    workoutContext?.workout.sections
                        .flatMap((s) => s.groups)
                        .flatMap((g) => g.exercises) ?? [];
                const currentExerciseIndex = allExercises.findIndex(
                    (ex) => ex.uuid === exerciseContext?.exercise.uuid,
                );
                const isLastExercise = currentExerciseIndex === allExercises.length - 1;
                const currentSetIndex = exerciseContext?.exercise.sets.findIndex(
                    (s) => s.uuid === handler.uuid,
                );
                const isLastSetOfExercise =
                    currentSetIndex ===
                    (exerciseContext?.exercise.sets.length ?? 0) - 1;
                const isLastSetOfWorkout = isLastExercise && isLastSetOfExercise;

                if (isLastSetOfWorkout) {
                    // Clear timer if this is the last set of the entire workout
                    workoutContext?.startRestTimer(null);
                } else if (exerciseContext?.exercise.rest_duration != null) {
                    // Start rest timer based on the restTimerBetweenExercises flag
                    if (restTimerBetweenExercises) {
                        // Timer between sets AND exercises (just not the last set of workout)
                        workoutContext?.startRestTimer(
                            exerciseContext.exercise.rest_duration,
                        );
                    } else if (!isLastSetOfExercise) {
                        // Timer only between sets (not on last set of each exercise)
                        workoutContext?.startRestTimer(
                            exerciseContext.exercise.rest_duration,
                        );
                    }
                }
            }}
            className="flex size-8 items-center justify-center rounded-full bg-stone-200/50"
        >
            <CheckIcon className="size-5 text-stone-300" aria-hidden="true" />
        </button>
    );
}

export function ExerciseSetFields({
    handler,
    readOnly: _workoutReadOnly,
    isTemplate,
    category,
    dropIndent = 0,
}: {
    handler: workouts.ExerciseSet;
    isTemplate: boolean;
    readOnly: boolean;
    // Strength-only: cardio renders through CardioSteady / CardioIntervals, never
    // here, so a cardio category can't even be passed.
    category: workouts.StrengthCategory;
    // Pixels the row's lead grew by for a DropLabel.
    dropIndent?: number;
}) {
    // Our set itself can be read only except the check mark without the workout
    // being read only.
    const readOnly = _workoutReadOnly || handler.status != "INCOMPLETE";

    const getName = <K extends keyof server.journal.workouts.ExerciseSet.output>(
        key: K,
    ) => {
        return `${handler.uuid}-${String(key)}`;
    };

    const setForm = forms.useForm(
        {
            fields: {
                distance: {kind: "decimal", name: getName("distance"), nullable: true},
                reps: {kind: "number", name: getName("reps"), nullable: true},
                weight: {kind: "weight", name: getName("weight"), nullable: true},
                rpe: {kind: "decimal", name: getName("rpe"), nullable: true},
                weight_percentage: {
                    kind: "decimal",
                    name: getName("weight_percentage"),
                    nullable: true,
                },
                duration: {kind: "duration", name: getName("duration"), nullable: true},
                duration_type: {
                    kind: "select",
                    nullable: true,
                    widget: "select",
                    options: [
                        ["SECONDS", "sec"],
                        ["MINUTES", "min"],
                        ["HOURS", "hours"],
                    ],
                    required: false,
                },
                distance_type: {
                    kind: "select",
                    nullable: true,
                    widget: "select",
                    options: [
                        ["INCHES", "in"],
                        ["YARDS", "yd"],
                        ["FEET", "ft"],
                        ["METERS", "m"],
                        ["MILES", "mi"],
                        ["KILOMETERS", "km"],
                    ],
                    required: false,
                },
            },
        },
        {
            value: {
                distance: handler.distance,
                reps: handler.reps,
                weight: handler.weight,
                rpe: handler.rpe,
                weight_percentage: handler.weight_percentage,
                duration: handler.duration,
                duration_type: handler.duration_type,
                distance_type: handler.distance_type,
            },
            onChange: handler.__update,
            readOnly,
        },
    );

    const isImperial = forms.useIsImperial();

    const hasRPE = handler.weight_type == "RPE";
    const hasPercentage = handler.weight_type == "PERCENTAGE";

    const medleyForm = forms.useField(
        {
            kind: "textarea",
            name: "instructions",
            nullable: true,
            placeholder: "Instructions for these sets...",
        },
        {
            value: handler.instructions,
            onChange: (instructions) => handler.__update({instructions}),
        },
    );

    const fieldClass = forms.getFieldClass({readOnly});
    const suffixClass = forms.getSuffixClass({readOnly});

    const check = isTemplate == false && (
        <ExerciseSetCheck
            readOnly={_workoutReadOnly}
            handler={handler}
            isImperial={isImperial}
            restTimerBetweenExercises={true}
        />
    );

    const {fields} = setForm;

    const durationField = <forms.Widget field={fields.duration} />;

    const distanceField = (className: string) => (
        <forms.Widget field={fields.distance} className={className} />
    );

    const repsField = (
        <widgets.NumberInput
            {...fields.reps}
            id={getName("reps")}
            integerOnly
            className={fieldClass}
        />
    );

    const rpeField = <forms.Widget field={fields.rpe} className={fieldClass} />;

    return match(category)
        .with("MEDLEY", () => (
            <div className="w-full space-y-1">
                <forms.Widget
                    field={{...medleyForm, readOnly: false}}
                    className={classNames(
                        fieldClass,
                        "min-h-[150px] sm:min-h[200px] block w-full rounded-md!",
                    )}
                />
                <div className="flex items-center gap-1">
                    {durationField}
                    <div>{check}</div>
                </div>
            </div>
        ))
        .with("REPS_DISTANCE", () => (
            <>
                <div className="flex flex-1 items-center gap-1">
                    <div className="flex w-full flex-1 rounded-md shadow-xs">
                        {distanceField(classNames(fieldClass, "sm:max-w-none"))}
                        <forms.Widget
                            field={fields.distance_type}
                            className={classNames(
                                fieldClass,
                                "form-select",
                                "-ml-1 rounded-l-none rounded-r-md text-xs disabled:opacity-100",
                            )}
                        />
                    </div>
                    {/* This will never be true, RPE is just for weight exercises */}
                    {!hasRPE && <Icon icon="custom-bolder-cross" />}

                    <div className="flex-1">
                        <label htmlFor={getName("reps")} className="sr-only">
                            Reps
                        </label>
                        <div className="flex rounded-md shadow-xs">
                            {repsField}
                            <span className={suffixClass}>Reps</span>
                        </div>
                    </div>
                </div>
                <div>{check}</div>
            </>
        ))
        .with(P.union("WEIGHT_DISTANCE", "WEIGHT_DURATION_DISTANCE"), () => (
            <>
                <div className="grid flex-1 grid-cols-[3fr_min-content_2fr] items-center gap-1">
                    <div className="flex rounded-md shadow-xs">
                        {distanceField(classNames(fieldClass, "sm:max-w-none"))}

                        <forms.Widget
                            field={fields.distance_type}
                            className={classNames(
                                fieldClass,
                                "form-select",
                                "w-[50px]! sm:w-32! flex-none!",
                                "-ml-1 rounded-l-none rounded-r-md text-xs disabled:opacity-100",
                            )}
                        />
                    </div>
                    <div className="text-xs">with</div>
                    <div>
                        <label htmlFor={getName("weight")} className="sr-only">
                            Weight
                        </label>
                        <div className="flex rounded-md shadow-xs">
                            <forms.Widget
                                field={fields.weight}
                                className={fieldClass}
                            />
                            <span className={suffixClass}>
                                {isImperial ? "lb" : "kg"}
                            </span>
                        </div>
                    </div>
                    {category == "WEIGHT_DURATION_DISTANCE" && durationField}
                </div>
                {check}
            </>
        ))
        .with("DURATION_DISTANCE", () => (
            <>
                <div className="flex flex-1 items-center gap-1">
                    <div className="flex w-full flex-1 rounded-md shadow-xs">
                        {distanceField(classNames(fieldClass, "sm:max-w-none"))}
                        <forms.Widget
                            field={fields.distance_type}
                            className={classNames(
                                fieldClass,
                                "form-select",
                                "-ml-1 rounded-l-none rounded-r-md text-xs disabled:opacity-100",
                            )}
                        />
                    </div>
                    <div className="flex-1">{durationField}</div>
                </div>
                <div>{check}</div>
            </>
        ))
        .with("DURATION", () => (
            <>
                <div
                    className={classNames(
                        "flex flex-col gap-1",
                        hasRPE == false && "sm:flex-row",
                    )}
                >
                    <div className="flex-1 sm:basis-3/5">{durationField}</div>
                    <div className="flex gap-1">
                        <label htmlFor={getName("weight")} className="sr-only">
                            Weight
                        </label>
                        <div className="flex flex-1 gap-1">
                            <div className="flex flex-1 rounded-md shadow-xs">
                                <forms.Widget
                                    field={fields.weight}
                                    className={fieldClass}
                                />
                                <span className={suffixClass}>
                                    {isImperial ? "lb" : "kg"}
                                </span>
                            </div>
                        </div>

                        {hasRPE && (
                            <>
                                <label htmlFor={getName("rpe")} className="sr-only">
                                    RPE
                                </label>
                                <div className="flex flex-1 rounded-md shadow-xs">
                                    {rpeField}
                                    <span className={suffixClass}>RPE</span>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <div>{check}</div>
            </>
        ))
        .with(P.union("WEIGHT", "BODYWEIGHT"), () => (
            <>
                {(isTemplate == false || hasPercentage == false) && (
                    <div className="flex-1">
                        <label htmlFor={getName("weight")} className="sr-only">
                            Weight
                        </label>
                        <div className="flex rounded-md shadow-xs">
                            {/* We use the status to remount in case we accept the suggested weight. */}
                            <forms.Widget
                                key={handler.status}
                                field={fields.weight}
                                readOnly={false}
                                placeholder={
                                    (handler.status === "INCOMPLETE"
                                        ? handler.suggested_weight
                                        : undefined) ??
                                    (category === "BODYWEIGHT" ? "BW" : undefined)
                                }
                                className={classNames(fieldClass, "")}
                            />
                            <span className={suffixClass}>
                                {isImperial ? "lb" : "kg"}
                            </span>
                        </div>
                    </div>
                )}
                {!hasRPE && !hasPercentage && <Icon icon="custom-bolder-cross" />}

                {/* Weight and reps split the row; this basis hands the drop
                indent to the weight field alone. */}
                <div className="flex-1" style={{flexBasis: dropIndent}}>
                    <label htmlFor={getName("reps")} className="sr-only">
                        Reps
                    </label>
                    <div className="flex rounded-md shadow-xs">
                        {repsField}
                        <span className={suffixClass}>Reps</span>
                    </div>
                </div>
                {hasPercentage && (
                    <div className="flex-1">
                        <label
                            htmlFor={getName("weight_percentage")}
                            className="sr-only"
                        ></label>

                        <div className="flex rounded-md shadow-xs">
                            <widgets.DecimalInput
                                {...fields.weight_percentage}
                                integerOnly
                                className={fieldClass}
                            />
                            <span className={suffixClass}>%</span>
                        </div>
                    </div>
                )}
                {hasRPE && (
                    <div className="flex-1">
                        <label htmlFor={getName("rpe")} className="sr-only">
                            RPE
                        </label>
                        <div className="flex rounded-md shadow-xs">
                            {rpeField}
                            <span className={suffixClass}>RPE</span>
                        </div>
                    </div>
                )}
                {check}
            </>
        ))
        .exhaustive();
}

type Side = server.journal.workouts.ExerciseSet.output["side"];

export const ExerciseSetType = (props: {
    count: number | null;
    position: number;
    type: models["core.models.ExerciseSet.Type"] | null;
    side: Side;
}) => {
    const badge = (className: string, content: React.ReactNode) => (
        <div
            aria-hidden="true"
            className={classNames(
                "relative flex size-8 items-center justify-center rounded-full font-semibold",
                className,
            )}
        >
            {content}
            {props.side != null && (
                <span className="absolute -right-1 -bottom-1 flex size-4 items-center justify-center rounded-full bg-gray-700 text-[9px] leading-none font-bold text-white ring-2 ring-stone-50 [text-box:trim-both_cap_alphabetic]">
                    {props.side == "LEFT" ? "L" : "R"}
                </span>
            )}
        </div>
    );

    if (props.count != null) {
        return badge("bg-blue-100 text-sm", `${props.count}x`);
    }

    return match(props.type)
        .with("WARM_UP", () => badge("bg-orange-100 text-xs", "W"))
        .with("REST", () => badge("bg-sky-100 text-xs", "R"))
        .with("COOL_DOWN", () => badge("bg-cyan-100 text-xs", "C"))
        .with("MAX", () =>
            badge("bg-orange-100 text-sm", <Icon icon="0222-medal-empty" />),
        )
        .with("FAILURE", () => badge("bg-red-100 text-sm", <Icon icon="0654-sad" />))
        .with("AMRAP", () => badge("bg-yellow-100 text-sm", <Icon icon="0516-rank3" />))
        .with(P.union("WORKING", "RPE", null), () =>
            badge("bg-blue-100 text-sm", props.position),
        )
        .exhaustive();
};

// The w-9 DropLabel widens a drop row's lead. ExerciseSetFields takes the extra
// width out of the weight field so the rest of the row stays put.
export const DROP_LABEL_WIDTH = 36;
// In place of the 14px drag handle.
export const DROP_INDENT_FOR_HANDLE = DROP_LABEL_WIDTH - 14;

export const DropLabel = () => (
    <span
        aria-hidden="true"
        className="w-9 text-center text-[10px] leading-none font-semibold tracking-wide text-gray-500 uppercase [text-box:trim-both_cap_alphabetic]"
    >
        Drop
    </span>
);

export const SideDescription = (props: {side: Side; isDrop: boolean}) => {
    const description = match(props)
        .with({isDrop: true, side: "LEFT"}, () => "Drop set, left")
        .with({isDrop: true, side: "RIGHT"}, () => "Drop set, right")
        .with({isDrop: true, side: null}, () => "Drop set")
        .with({isDrop: false, side: "LEFT"}, () => "Left side")
        .with({isDrop: false, side: "RIGHT"}, () => "Right side")
        .with({isDrop: false, side: null}, () => null)
        .exhaustive();

    return description == null ? null : <span className="sr-only">{description}</span>;
};

export function ExerciseSetsPreview({
    exercise,
    readOnly,
}: {
    exercise: {
        type: {
            category: server.journal.workouts.ExerciseType.output["category"];
        };
        sets: Array<
            | workouts.ExerciseSet
            | server.journal.workouts.ExerciseSet.output
            | server.journal.workouts.ExerciseSet.input
        >;
    };
    readOnly?: boolean;
}) {
    const {category} = exercise.type;

    // The fields preview only knows how to draw strength sets; cardio has its own
    // editors and isn't previewed field-by-field, so show its one-line session
    // summary instead. This also narrows `category` to StrengthCategory below.
    if (category == "CARDIO") {
        return (
            <div className="text-sm text-gray-600">{cardioSummary(exercise.sets)}</div>
        );
    }

    return (
        <>
            {exercise.sets.map((set, exerciseSetIndex) => {
                const side = "side" in set ? set.side : null;
                const isDrop = getDropParent(exercise.sets, exerciseSetIndex) != null;

                return (
                    <div key={set.uuid} className="flex items-center gap-2">
                        {isDrop && <DropLabel />}
                        <div>
                            <SideDescription side={side} isDrop={isDrop} />
                            <ExerciseSetType
                                count={null}
                                type={set.type}
                                side={side}
                                position={getSetPosition(
                                    exercise.sets,
                                    exerciseSetIndex,
                                )}
                            />
                        </div>
                        <ExerciseSetFields
                            handler={
                                "__update" in set
                                    ? set
                                    : {
                                          ...set,
                                          suggested_weight: null,
                                          __update: () => {},
                                      }
                            }
                            isTemplate={true}
                            readOnly={readOnly ?? true}
                            category={category}
                            // The label plus this row's gap-2.
                            dropIndent={isDrop ? DROP_LABEL_WIDTH + 8 : 0}
                        />
                    </div>
                );
            })}
        </>
    );
}
